import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Option, SubmissionOpportunity, AcademicReportSubmissionService, SubmissionType, AppVersionService } from '../../domain';
import { DatePipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { timeAgo } from '../../widgets/time-ago.fn';
import { ConsoleService } from 'app/widgets/console.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface SubmissionBtnDisplayConfig {
    calendarName: string;
    toSubmitText: string;
    toSubmitIconClass: string | null;
    submittedText: string;
    submittedIconClass: string | null;
    dueDateWarning?: string | null;
}

@Component({
    selector: 'academic-report-submission',
    templateUrl: './academic-report-submission.component.html',
    styleUrls: ['./academic-report-submission.component.scss']
})
export class AcademicReportSubmissionComponent implements OnInit, OnDestroy {
    
    private _destroyed$ = new Subject<void>();
    private hasValidSubmissionType = true;

    opportunity: Option<SubmissionOpportunity> = Option.create<SubmissionOpportunity>();
    config: SubmissionBtnDisplayConfig;

    @Input() submissionType: SubmissionType;
    @Input() btnClass: string = "";
    @Input() disabled: boolean = false;

    constructor(
        private readonly datePipe: DatePipe,
        private readonly svc: AcademicReportSubmissionService,
        private readonly snackbar: MatSnackBar,
        private readonly console: ConsoleService,
        private readonly appVersion: AppVersionService
    ) {}

    ngOnInit(){
        if (!this.submissionType) {
            this.console.error("AcademicReportSubmissionComponent: submissionType is required");
            this.hasValidSubmissionType = false;
            return;
        }

        this.svc.getSubmissionOpportunity(this.submissionType)
            .pipe(takeUntil(this._destroyed$))
            .subscribe(op => {
                this.opportunity = Option.create(op);
                this.updateDisplayState(op);
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    private updateDisplayState(op: SubmissionOpportunity) {

        const yearLabel = op.federalFiscalYear?.label ?? '';

        switch(this.submissionType) {
            case(SubmissionType.ThemesProjectsActivitiesANRMMP):
                this.config = {
                    calendarName: "federal fiscal year",
                    toSubmitText: "Submit for ANR Annual Organizational Reporting",
                    toSubmitIconClass: "fa fa-fw fa-send",
                    submittedText: `Submitted for ${yearLabel} organizational reporting`,
                    submittedIconClass: "fa fa-fw fa-check-square-o",
                    dueDateWarning: "Submissions after this date *will not* be included in the final report."
                };
                break;
            case(SubmissionType.CountyFTE):
            case(SubmissionType.ConditionChangeFTE):
                this.config = {
                    calendarName: "state fiscal year",
                    toSubmitText: "Submit",
                    toSubmitIconClass: "fa fa-fw fa-send",
                    submittedText: "Submitted",
                    submittedIconClass: "fa fa-fw fa-check-square-o",
                    dueDateWarning: "At any time, you may return to Project Board F.T.E., edit, and click submit again."
                };
                break;				
            case(SubmissionType.ThemesProjectsActivitiesCampusMMP):
                this.config = {
                    calendarName: "federal fiscal year",
                    toSubmitText: `Submit for ${yearLabel}`,
                    toSubmitIconClass: null,
                    submittedText: `You submitted for ${yearLabel}`,
                    submittedIconClass: "fa fa-fw fa-check-square-o",
                    dueDateWarning: "Submissions after this date *will not* be included in the final report."
                };
                break;
            default:
                this.config = {
                    calendarName: "federal fiscal year",
                    toSubmitText: "Submit",
                    toSubmitIconClass: null,
                    submittedText: "Submitted",
                    submittedIconClass: null
                };
                break;
        }
    }

    get isVisible(): boolean {

        if (!this.hasValidSubmissionType) {
            return false;
        }
        
        // Display the button starting on 9/1 and hide on 1/1
        // Note: Months are 0-based
        if (this.submissionType === SubmissionType.ThemesProjectsActivitiesCampusMMP) {
            const now = this.today;
            const start = new Date(now.getFullYear(), 8, 1);
            const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
            return now >= start && now <= end;
        }
        
        return true;
    }

    get isDisabled(): boolean {

        return this.disabled || (
            // Disable if no dueBy date is set for organizational reports
            this.isOrganizationalReport &&
            this.opportunity.match(
                () => true,
                (o: SubmissionOpportunity) => o.federalFiscalYear.submissionsDueBy.match(
                    () => true,
                    _ => false
                )
            )
        );
    }

    private get today(): Date {
        return this.appVersion.effectiveNow;
    }

    get tooltipText(): string {
        return this.opportunity.match(
            () => "",
            (o: SubmissionOpportunity) => !!o.submittedAt
                ? 'You have already submitted'
                : 'You have not yet submitted'
        );
    }

    get submitted(){
        return this.opportunity
        .match(
            () => false,
            (o: SubmissionOpportunity) => !!o.submittedAt);
    }

    submit() {
        this.opportunity.matchDo(
            () => { },
            (o: SubmissionOpportunity) => {
                const msg = this.getConfirmMessage(o);

                if (!confirm(msg)) {
                    return;
                }

                // Do not pass timezone offset. Backend will treat simulated date as Pacific Time.
                const simulatedDateTimeString = this.datePipe.transform(this.appVersion.simulatedDateTime, "yyyy-MM-dd'T'HH:mm:ss") || undefined;

                this.svc.submit(this.submissionType, o.federalFiscalYear.id, simulatedDateTimeString).then(result => {
                    result.matchDo(
                        _ => this.snackbar.open('Submitted successfully.'),
                        (error: string) => this.snackbar.open('Unable to submit: ' + error)
                    );
                });
            });
    }

    private get isOrganizationalReport(): boolean {
        return this.submissionType === SubmissionType.ThemesProjectsActivitiesANRMMP || 
               this.submissionType === SubmissionType.ThemesProjectsActivitiesCampusMMP;
    }

    private getConfirmMessage(o: SubmissionOpportunity): string {
        const mainMsg = !!o.submittedAt
            ? `You submitted ${timeAgo(o.submittedAt, this.today)} (on ${this.datePipe.transform(o.submittedAt)} at ${this.datePipe.transform(o.submittedAt, 'h:mm:ss aa')}).\n\nRe-submit?`
            : this.isOrganizationalReport                
                ? `Submit for the ${o.federalFiscalYear.label} ${this.config.calendarName}?`
                : 'Submit your changes?'

        const dueDateLines = this.isOrganizationalReport
            ? o.federalFiscalYear.submissionsDueBy.match(
                () => [],
                (d) => [`Your due date for organizational reporting is ${this.datePipe.transform(d)}.`]
            )
            : [];

        const notes = [...dueDateLines, this.config.dueDateWarning].filter(n => !!n);

        return notes.length > 0
            ? `${mainMsg}\n\nNote:\n${notes.join('\n')}`
            : mainMsg;
    }
}
